import { TransactionResponse } from 'ethers';
import { Shop__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import type { ContractCallParams } from '~/types/Web3Provider';
import { useWalletInfo, useWeb3ModalAccount } from '@web3modal/ethers/vue';
import useSendContractMethod from '~/composables/useSendContractMethod';
import {
  getCancelErrorMessage,
  modifyHistoryHashStatus,
  saveHashToLocalStorage,
  useEnvs,
  useMainStore
} from '#imports';
import { BigNumber } from 'bignumber.js';
import { apiUrls } from '~/utils/constants';
import type { SellerActivityResponse } from '~/types/seller';
import { Formatters } from '~/utils/formatters/formatters';

interface IUseBuyToken {
  buyToken: (index: number, countTokens: number, price: number, tokenAddress: string) => Promise<void>;
  isButtonLoading: Ref<boolean>;
  showInsufficientFundsDialog: Ref<boolean>;
  showErrorDialog: Ref<boolean>;
  showSuccessDialog: Ref<boolean>;
  linkToTransaction: Ref<string>;
  countTokensPurchased: Ref<number>;
  transactionPrice: Ref<number>;
  errorMessage: Ref<string>;
}

export default async (): Promise<IUseBuyToken> => {
  const { t } = useI18n();
  const { address } = useWeb3ModalAccount();
  const { walletInfo } = useWalletInfo();
  const { sendContractMethod } = useSendContractMethod();
  const { blockchain, apiUrl } = useEnvs();
  const store = useMainStore();

  const { allowanceShop } = useTokensReader();
  const { approveSpendUsdt } = useStakingMethods();
  const usdtContract = ref(blockchain.contracts.usdt);
  const { balance } = await useBalance(usdtContract);
  const { getSigner } = useProvider();

  const isButtonLoading = ref(false);
  const showInsufficientFundsDialog = ref(false);
  const showErrorDialog = ref(false);
  const showSuccessDialog = ref(false);
  const errorMessage = ref('');
  const linkToTransaction = ref('');
  const countTokensPurchased = ref(0);
  const transactionPrice = ref(0);

  const sendContractMethodExtended = async <T extends Record<any, any>, M extends keyof T>(
    sendParams: ContractCallParams<T, M>,
    onConfirmation?: (confirmations?: number) => void
  ) => {
    const from = address.value;
    return sendContractMethod({ ...sendParams, from }, onConfirmation);
  };

  const buyTokenContract = async (tokenIndex: number, countTokens: string) => {
    return sendContractMethodExtended({
      contract: Shop__factory.connect(blockchain.contracts.shop, await getSigner()),
      methodName: 'buy',
      args: [tokenIndex, countTokens]
    });
  };

  const buyToken = async (tokenIndex: number, countTokens: number, price: number, tokenAddress: string) => {
    try {
      if (!balance.value || !address.value) {
        return;
      }

      isButtonLoading.value = true;

      const isEnoughBalance = new BigNumber(balance.value).gte(BigNumber(price));

      if (!isEnoughBalance) {
        transactionPrice.value = price;
        showInsufficientFundsDialog.value = true;
        return;
      }

      const allowedCount = await allowanceShop(address.value);

      const shouldCallApprove = new BigNumber(allowedCount).isLessThan(price);

      if (shouldCallApprove) {
        const transactionApprove = await approveSpendUsdt(price, blockchain.contracts.shop);

        if (!transactionApprove?.hash) {
          return;
        }

        saveHashToLocalStorage(t('notificationApprove'), transactionApprove.hash);
        store.updateVersion();

        await transactionApprove?.wait();
      }

      const transactionBuy: TransactionResponse | undefined = await buyTokenContract(tokenIndex, String(countTokens));

      if (!transactionBuy?.hash) {
        return;
      }

      const token = tokenMetaData(blockchain.contracts, tokenAddress);

      saveHashToLocalStorage(
        `${t('notificationBuy')} (${countTokens} ${Formatters.text.splitCamelCase(token?.methodName).toUpperCase()})`,
        transactionBuy.hash
      );

      store.updateVersion();

      await transactionBuy.wait();

      useFetch<SellerActivityResponse>(apiUrls.streetMerchant, {
        baseURL: apiUrl,
        method: 'POST',
        body: {
          userAddress: address,
          tokenAddress,
          transactionHash: transactionBuy.hash,
          amount: countTokens
        }
      });

      modifyHistoryHashStatus(transactionBuy?.hash, 'Done');

      store.updateVersion();
      store.updateBuypackCounter();

      countTokensPurchased.value = countTokens;
      transactionPrice.value = price;

      showSuccessDialog.value = true;
      linkToTransaction.value = blockchain.blockScanUrl + '/tx/' + transactionBuy?.hash;
    } catch (error: unknown) {
      const currentProviderKind = walletInfo.value?.name;

      if (currentProviderKind) {
        errorMessage.value = getCancelErrorMessage(error, currentProviderKind);
      }

      showErrorDialog.value = true;

      console.error(error);
    } finally {
      isButtonLoading.value = false;
    }
  };

  return {
    buyToken,
    showInsufficientFundsDialog,
    showErrorDialog,
    showSuccessDialog,
    isButtonLoading,
    countTokensPurchased,
    transactionPrice,
    linkToTransaction,
    errorMessage
  };
};
